import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import EmployeePicker from "@/components/app/EmployeePicker";
import { toast } from "sonner";
import { Pencil, Plus, Users } from "lucide-react";

const FIELDS = [["name", "Name *"], ["employee_code", "Employee ID"], ["department", "Department"], ["designation", "Designation"],
  ["joining_date", "Joining Date", "date"], ["phone", "Phone"], ["email", "Email"]];

export const HrEmployees = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/leave/employees").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/leave/users-lite").then((r) => setUsers(r.data)); }, []);
  const importUsers = async () => {
    const r = await api.post("/leave/employees/import-users");
    toast.success(`${r.data.created} employee record(s) created from logins`);
    load();
  };
  const shown = rows.filter((e) => `${e.name} ${e.employee_code || ""} ${e.department || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="hr-employees">
      <div className="flex flex-wrap gap-2 mb-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="rounded-none h-9 text-xs w-56" data-testid="hr-emp-search" />
        <Button variant="outline" onClick={importUsers} className="rounded-none h-9 text-xs uppercase tracking-widest font-bold ml-auto" data-testid="hr-emp-import"><Users className="w-4 h-4 mr-1" /> Import from Users</Button>
        <Button onClick={() => setEdit({ active: true })} className="rounded-none h-9 bg-[#002FA7] text-xs uppercase tracking-widest font-bold" data-testid="hr-emp-add"><Plus className="w-4 h-4 mr-1" /> Add Employee</Button>
      </div>
      <div className="border border-[#E5E7EB] overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
            <tr>{["Name", "Emp ID", "Department", "Designation", "Joining", "Login", "Reporting Manager", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2 label-eyebrow whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-[#9CA3AF]">No employees yet — use "Import from Users" or "Add Employee".</td></tr>}
            {shown.map((e) => (
              <tr key={e.id} className="border-b border-[#F3F4F6]" data-testid={`hr-emp-row-${e.id}`}>
                <td className="px-3 py-2 font-bold">{e.name}</td>
                <td className="px-3 py-2 text-xs">{e.employee_code || "—"}</td>
                <td className="px-3 py-2 text-xs">{e.department || "—"}</td>
                <td className="px-3 py-2 text-xs">{e.designation || "—"}</td>
                <td className="px-3 py-2 text-xs">{e.joining_date ? fmtDate(e.joining_date) : <span className="text-[#B45309]">Not set</span>}</td>
                <td className="px-3 py-2 text-xs">{e.user_name || "—"}</td>
                <td className="px-3 py-2 text-xs">{e.reporting_manager_name || "— (HR only)"}</td>
                <td className="px-3 py-2 text-xs">{e.active === false ? "Inactive" : "Active"}</td>
                <td className="px-3 py-2"><button onClick={() => setEdit(e)} className="text-[#002FA7]" data-testid={`hr-emp-edit-${e.id}`}><Pencil className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <EmployeeDialog emp={edit} users={users} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};

const EmployeeDialog = ({ emp, users, onClose, onSaved }) => {
  const [f, setF] = useState(emp);
  const save = async () => {
    const body = {};
    [...FIELDS.map(([k]) => k), "user_id", "reporting_manager_user_id", "active"].forEach((k) => { if (f[k] !== undefined) body[k] = f[k] === "" ? null : f[k]; });
    try {
      if (emp.id) await api.patch(`/leave/employees/${emp.id}`, body);
      else await api.post("/leave/employees", body);
      toast.success("Employee saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hr-emp-dialog">
        <DialogHeader><DialogTitle className="font-black tracking-tight">{emp.id ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, l, type]) => (
            <div key={k}>
              <Label className="label-eyebrow">{l}</Label>
              <Input type={type || "text"} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="rounded-none mt-1 h-10" data-testid={`emp-${k}`} />
            </div>
          ))}
          <div>
            <Label className="label-eyebrow">Linked Login (for My Leaves)</Label>
            <div className="mt-1"><EmployeePicker employees={users} value={f.user_id || ""} onChange={(id) => setF({ ...f, user_id: id || null })} placeholder="Select login…" testid="emp-user" /></div>
          </div>
          <div>
            <Label className="label-eyebrow">Reporting Manager</Label>
            <div className="mt-1"><EmployeePicker employees={users} value={f.reporting_manager_user_id || ""} onChange={(id) => setF({ ...f, reporting_manager_user_id: id || null })} placeholder="None — straight to HR" testid="emp-manager" /></div>
          </div>
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
            <Switch checked={f.active !== false} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="emp-active" /> Active
          </label>
        </div>
        <Button onClick={save} className="w-full rounded-none bg-[#002FA7] uppercase tracking-widest text-xs font-bold h-11 mt-2" data-testid="emp-save">Save Employee</Button>
      </DialogContent>
    </Dialog>
  );
};
